import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { COURSES } from '../data/curriculum';

export interface Track {
    id: string;
    title: string;
    src: string;
    courseId?: string; // If undefined, it's a global/menu track
}

// Define the Playlist
export const MUSIC_TRACKS: Track[] = [
    { id: 'menu', title: 'Gorilla Basecamp', src: '/assets/music/a_small_fire_will_do.wav' },
    { id: 'digital', title: 'Digital Dawn', src: '/assets/music/Jungle trail bpm90 C Example of Main Menu.wav', courseId: 'digital-foundations' },
    { id: 'foundation', title: 'Banana Boogie', src: '/assets/music/Banana Craziness.mp3', courseId: 'foundation' },
    { id: 'production', title: 'Assembly Line Beats', src: '/assets/music/Jungle.mp3', courseId: 'production' },
    { id: 'security', title: 'Encryption Keys', src: '/assets/music/elvin-secret_code.wav', courseId: 'cyber-security' },
    { id: 'logic', title: 'Neural Network', src: '/assets/music/hunter-class_lifeform.wav', courseId: 'logic-architect' },
    { id: 'systems', title: 'Server Room Trance', src: '/assets/music/Jungle Trance.wav', courseId: 'sovereign-systems' },
    { id: 'data', title: 'Query Quest', src: '/assets/music/rumble_jungle.wav', courseId: 'data-detective' },
    { id: 'game-dev', title: '8-Bit Adventure', src: '/assets/music/A New Camp NES F.wav', courseId: 'game-dev' },
    { id: 'ai', title: 'Cyber Synth', src: '/assets/music/Jungle trail bpm90 C Example Background.wav', courseId: 'ai-operator' },
    { id: 'ui-ux', title: 'Creative Studio', src: '/assets/music/rhodes.mp3', courseId: 'ui-ux' },
    { id: 'automation', title: 'Global Circuit', src: '/assets/music/Around The World NES.wav', courseId: 'automation' },

    // Special Areas
    { id: 'arcade', title: 'Neural Arcade', src: '/assets/music/hunter-class_lifeform.wav' },

    // Fallbacks or extras
    { id: 'extra-1', title: 'Deep Canopy Drums', src: '/assets/music/Jungle trail bpm90 C Example with animals(1).wav' },
    { id: 'classic-menu', title: 'Classic Menu', src: '/assets/music/menu_theme.wav' },
    { id: 'extra-3', title: 'Crystal Logic', src: '/assets/music/Harp.mp3' },
];

interface MusicStore {
    currentTrack: Track;
    isPlaying: boolean;
    volume: number;
    isMuted: boolean;

    // Actions
    playTrack: (trackId: string) => void;
    togglePlay: () => void;
    setVolume: (val: number) => void;
    toggleMute: () => void;

    // Auto-switcher logic
    playCourseTheme: (courseId: string) => void;
    syncTrackWithPath: (pathname: string) => void;
}

export const useMusicStore = create<MusicStore>()(
    persist(
        (set, get) => ({
            currentTrack: MUSIC_TRACKS[0], // Default to Menu
            isPlaying: false, // Don't auto-play on load to be polite
            volume: 0.1, // 10% default (Low background)
            isMuted: false,

            playTrack: (trackId) => {
                const track = MUSIC_TRACKS.find(t => t.id === trackId);
                if (track) {
                    set({ currentTrack: track, isPlaying: true });
                }
            },

            togglePlay: () => set(state => ({ isPlaying: !state.isPlaying })),

            setVolume: (val) => set({ volume: Math.max(0, Math.min(1, val)) }),

            toggleMute: () => set(state => ({ isMuted: !state.isMuted })),

            playCourseTheme: (courseId) => {
                // Find track for this course
                const track = MUSIC_TRACKS.find(t => t.courseId === courseId);
                const current = get().currentTrack;

                // Only switch if it's a different track and we have a specific theme for this course
                if (track && current.id !== track.id) {
                    set({ currentTrack: track, isPlaying: true });
                }
            },

            syncTrackWithPath: (pathname) => {
                // Remove leading slash
                const path = pathname.startsWith('/') ? pathname.substring(1) : pathname;

                if (!path || path === '/') {
                    // Back to menu theme? Maybe, or keep playing.
                    // Let's enforce Menu Theme on dashboard to feel "homey"
                    get().playTrack('menu');
                    return;
                }

                if (path.startsWith('games')) {
                    get().playTrack('arcade');
                    return;
                }

                // Find which course contains this module/path
                const course = COURSES.find(c => c.modules.some(m => m.id === path || m.path === path));

                if (course) {
                    get().playCourseTheme(course.id);
                }
            }
        }),
        {
            name: 'gorilla-radio-storage',
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({ volume: state.volume, isMuted: state.isMuted }), // Don't persist track/playing state
        }
    )
);
